import React from "react"
import { useTranslation } from "react-i18next"
import { styled } from "@mui/material/styles"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"
import { PWA_INSTALLATION_GUIDE } from "constants/storages"
import { iOS } from "constants/index"
import { getOS } from "utils/navigatorUtils"
import IOSShareIcon from "components/atoms/_icons/IOSShareIcon"
import MoreIcon from "components/atoms/_icons/MoreIcon"
import AddSquareIcon from "components/atoms/_icons/AddSquareIcon"
import Card from "components/atoms/Card"
import CTA from "components/atoms/CTA"

export default function PWAInstallationGuide() {
  const { t } = useTranslation()

  function handleUnderstoodButtonClicked() {
    localStorage.setItem(PWA_INSTALLATION_GUIDE, String(Date.now()))
    document.dispatchEvent(new Event("storage"))
  }

  function getGuideSteps() {
    const isIOS = getOS() === iOS

    return <>
      {getFirstGuideStep(isIOS)}
      {getSecondGuideStep(isIOS)}
      <GuideText>3. {t("pwaInstallationGuide.step_3")}</GuideText>
    </>
  }

  function getFirstGuideStep(isIOS: boolean) {
    if (isIOS) {
      return <GuideText>1. {t("pwaInstallationGuide.step_1_1_iOS")} <IOSShareIcon/> {t("pwaInstallationGuide.step_1_2")}
      </GuideText>
    } else {
      return <GuideText className="android">1. {t("pwaInstallationGuide.step_1_1_android")}
        <MoreIcon/> {t("pwaInstallationGuide.step_1_2")}
      </GuideText>
    }
  }

  function getSecondGuideStep(isIOS: boolean) {
    if (isIOS) {
      return <GuideText>2. {t("pwaInstallationGuide.step_2_1")} <AddSquareIcon/> {t("pwaInstallationGuide.step_2_2")}
      </GuideText>
    } else {
      return <GuideText>2. {t("pwaInstallationGuide.step_2_1")} {t("pwaInstallationGuide.step_2_2")}
      </GuideText>
    }
  }

  return (
    <Container id="pwa-installation-guide">
      <Title>
        {t("pwaInstallationGuide.title")}
      </Title>
      <Card>
        {getGuideSteps()}
        <CTA
          variant="contained"
          onClick={handleUnderstoodButtonClicked}
        >
          {t("general.understood")}
        </CTA>
      </Card>
    </Container>
  )
}

const Container = styled(Stack)(({ theme }) => ({
  width: "100vw",
  maxWidth: "var(--max-container-width)",
  height: "100vh",
  overflow: "hidden",
  padding: "16px",
  justifyContent: "center",
  alignItems: "center",
  backgroundColor: theme.palette.gray["2"],

  "& button": {
    marginTop: "60px"
  },

  "@supports (height: 100dvh)": {
    height: "calc(100dvh)"
  }
}))

const Title = styled(Typography)(({ theme }) => ({
  fontSize: "1.4rem",
  fontWeight: 500,
  color: theme.palette.gray["15"],
  textAlign: "center",
  marginBottom: "12px"
}))

const GuideText = styled(Typography)(({ theme }) => ({
  fontSize: "1.2rem",
  fontWeight: 400,
  color: theme.palette.gray["15"],
  marginTop: "12px",

  "&.android": {
    "& svg": {
      transform: "rotate(-90deg)"
    }
  },

  "& svg": {
    stroke: theme.palette.primary["6"],
    verticalAlign: "middle"
  }
}))
